import { AppState } from '../../types';
import { CONNECTOR } from '../../utils/constants';
import { connectorHeight } from '../../utils/connectorLayout';
import { GeometryUtils, connectorCenter } from '../../utils/geometry';

/** Minimum distance a dropped node must keep from existing connectors and nodes. */
const NODE_PLACEMENT_TOLERANCE = 20;

/**
 * Returns true when (x, y) is far enough from every connector and node to place
 * a new harness node there.
 */
export function isValidNodePosition(state: AppState, x: number, y: number): boolean {
  const tolerance = NODE_PLACEMENT_TOLERANCE;

  for (const connector of state.connectors) {
    const dx = x - connector.xPosition;
    const dy = y - connector.yPosition;
    const height = connectorHeight(connector, state.wires);
    if (dx >= -tolerance && dx <= CONNECTOR.WIDTH + tolerance && dy >= -tolerance && dy <= height + tolerance) {
      return false;
    }
  }

  for (const node of state.nodes) {
    const dx = x - node.xPosition;
    const dy = y - node.yPosition;
    if (Math.sqrt(dx * dx + dy * dy) < tolerance) {
      return false;
    }
  }

  return true;
}

/**
 * Finds the path index of the segment closest to (nodeX, nodeY), i.e. the best
 * place to splice a new node into the wire's path.
 */
export function calculateOptimalInsertIndex(state: AppState, wire: any, nodeX: number, nodeY: number): number {
  const startConnector = state.connectors.find(t => t.id === wire.startConnectorId);
  const endConnector = state.connectors.find(t => t.id === wire.endConnectorId);

  if (!startConnector || !endConnector) {
    return wire.path.length;
  }

  const pathPoints = [
    connectorCenter(startConnector),
    ...wire.path.map((nodeId: string) => {
      const node = state.nodes.find(n => n.id === nodeId);
      return node ? { x: node.xPosition, y: node.yPosition } : null;
    }).filter(Boolean),
    connectorCenter(endConnector)
  ];

  let bestIndex = 0;
  let minDistance = Infinity;

  for (let i = 0; i < pathPoints.length - 1; i++) {
    const distance = GeometryUtils.distanceToLine({ x: nodeX, y: nodeY }, pathPoints[i], pathPoints[i + 1]);
    if (distance < minDistance) {
      minDistance = distance;
      bestIndex = i;
    }
  }

  return bestIndex;
}
